import React, { useEffect, useState } from "react";
import {
  CustomTextInput,
  CustomDropdown,
  CustomCheckbox,
} from "../../../../components";
import BillingSummary from "../BillingSummary";
import { AxiosInstance, POSTAL_PINCODE_API } from "../../../../services/Api";
import { SCHOOL_BOARD_OPTIONS, SCHOOL_CATEGORY_OPTIONS } from "../pricingData";
import styles from "../Pricing.module.css";

// ---------- Validation regex ----------
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NAME_ALLOWED_REGEX = /^[A-Za-z\s]*$/;
const SCHOOL_NAME_ALLOWED_REGEX = /^[A-Za-z0-9\s.&'\-]*$/;
const PHONE_FULL_REGEX = /^[6-9]\d{9}$/;
// UDISE Code: exactly 11 digits (India standard)
const UDISE_REGEX = /^\d{11}$/;
// GSTIN: 15 chars — 2 digits + 5 letters + 4 digits + 1 letter + 1 alphanumeric + Z + 1 alphanumeric
const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
// Short name: only lowercase letters, digits, hyphens (no dots, slashes, protocols)
const SHORTNAME_REGEX = /^[a-z0-9]([a-z0-9-]{1,48}[a-z0-9])?$/;
// Detect URL-like input in short name (dots, slashes, protocols, www)
const URL_LIKE_REGEX = /(https?:\/\/|www\.|\.|\/)/i;

const OTP_RESEND_SECONDS = 120;
const SHORTNAME_BASE_URL = "ifillip.com";

// Email verification (OTP is generated & validated by the backend).
// Full URL: {API_BASE_URL}/saas/send-email-verification
const EMAIL_VERIFICATION_ENDPOINT = "saas/send-email-verification";
const EMAIL_VERIFICATION_SEND = 0; // status: 0 → send the code
const EMAIL_VERIFICATION_VERIFY = 1; // status: 1 → verify the entered code

// ---------- States master (DB) config ----------
const DEFAULT_COUNTRY_ID = 101;
// Full URL: https://dev.ifillip.com/api/v1/api/school/states
const STATES_ENDPOINT = "/school/states";

const formatTimer = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60)
    .toString()
    .padStart(2, "0");
  const seconds = (totalSeconds % 60).toString().padStart(2, "0");
  return `${minutes}:${seconds}`;
};

const buildOption = (label) => ({
  value: label.toLowerCase().replace(/\s+/g, "-"),
  label,
});

const uniqueOptions = (labels) => {
  const options = [];
  labels.filter(Boolean).forEach((label) => {
    const option = buildOption(label);
    if (!options.some((opt) => opt.value === option.value)) {
      options.push(option);
    }
  });
  return options;
};

// Normalize a state name so postal-API names line up with DB names.
const normalizeStateName = (name) =>
  (name || "")
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z]/g, ""); // drops spaces, dots, hyphens, etc.

// Match the postal-API state name against the DB states list.
const matchStateRecord = (postalStateName, statesList) => {
  const target = normalizeStateName(postalStateName);
  if (!target) return null;

  // 1) exact normalized match
  let found = statesList.find((s) => normalizeStateName(s.name) === target);
  if (found) return found;

  // 2) fallback: one contains the other (handles minor naming diffs)
  found = statesList.find((s) => {
    const dbName = normalizeStateName(s.name);
    return dbName.includes(target) || target.includes(dbName);
  });

  return found || null;
};

// ---------- Sanitizers ----------
const sanitizeName = (value) => value.replace(/[^A-Za-z\s]/g, "");
const sanitizeSchoolName = (value) => value.replace(/[^A-Za-z0-9\s.&'\-]/g, "");
const sanitizePhone = (value) => {
  let digits = value.replace(/\D/g, "");
  // Trim any leading 0/1/2/3/4/5 chunks — first kept digit must be 6-9
  digits = digits.replace(/^[0-5]+/, "");
  return digits.slice(0, 10);
};

const handlePhoneKeyDown = (e) => {
  // Let modifiers, arrows, backspace, tab, etc. through
  if (
    (e.ctrlKey || e.metaKey) &&
    ["a", "c", "v", "x", "z"].includes(e.key.toLowerCase())
  )
    return;
  const allowed = new Set([
    "Backspace",
    "Delete",
    "Tab",
    "ArrowLeft",
    "ArrowRight",
    "Home",
    "End",
    "Enter",
  ]);
  if (allowed.has(e.key)) return;

  // Block leading space (existing behavior)
  if (e.key === " " && e.target.selectionStart === 0) {
    e.preventDefault();
    return;
  }

  if (/^\d$/.test(e.key)) {
    const currentValue = e.target.value || "";
    const selectionStart = e.target.selectionStart ?? 0;

    // ✅ First digit must be 6, 7, 8, or 9
    if (currentValue === "" && !"6789".includes(e.key)) {
      e.preventDefault();
    }
    // ✅ Also block "0"-"5" when cursor is at position 0 with existing content
    //    (typing before a valid number → would create invalid leading digit)
    if (
      selectionStart === 0 &&
      currentValue.length > 0 &&
      !"6789".includes(e.key)
    ) {
      e.preventDefault();
    }
    return;
  }

  e.preventDefault();
};
const sanitizeUdise = (value) => value.replace(/\D/g, "").slice(0, 11);
const sanitizeGstin = (value) =>
  value
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, "")
    .slice(0, 15);
// Short name: just lowercase and cap length — DO NOT strip dots/slashes so we can
// detect URL-like input and show the correct error message.
const sanitizeShortName = (value) => value.toLowerCase().slice(0, 50);

const Step3SchoolDetails = ({
  schoolDetails,
  onFieldChange,
  addOns,
  selectedAddOnIds,
  plan,
  slab,
  onBack,
  onNext,
}) => {
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [emailHelperText, setEmailHelperText] = useState("");
  const [otpError, setOtpError] = useState("");
  const [resendSeconds, setResendSeconds] = useState(0);
  const [adminEmailError, setAdminEmailError] = useState("");

  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [adminFirstNameError, setAdminFirstNameError] = useState("");
  const [adminLastNameError, setAdminLastNameError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [adminPhoneError, setAdminPhoneError] = useState("");
  const [schoolNameError, setSchoolNameError] = useState("");
  const [schoolBoardError, setSchoolBoardError] = useState("");
  const [schoolCategoryError, setSchoolCategoryError] = useState("");
  const [cityError, setCityError] = useState("");
  const [stateError, setStateError] = useState("");
  const [addressError, setAddressError] = useState("");
  const [udiseError, setUdiseError] = useState("");
  const [gstinError, setGstinError] = useState("");

  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [shortNameError, setShortNameError] = useState("");
  const [shortNameHelperText, setShortNameHelperText] = useState("");
  const [shortNameSuggestions, setShortNameSuggestions] = useState([]);

  const emailVerified = schoolDetails.emailVerified;
  const shortNameAvailable = schoolDetails.shortNameVerified;

  const [cityOptions, setCityOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [pincodeLookupLoading, setPincodeLookupLoading] = useState(false);
  const [pincodeError, setPincodeError] = useState("");

  // ---------- States master (from DB) ----------
  const [statesList, setStatesList] = useState([]);
  const statesListRef = React.useRef([]);

  const isEmailFormatValid = EMAIL_REGEX.test(
    (schoolDetails.email || "").trim(),
  );

  useEffect(() => {
    if (resendSeconds <= 0) return undefined;
    const timerId = setTimeout(() => {
      setResendSeconds((prev) => prev - 1);
    }, 1000);
    return () => clearTimeout(timerId);
  }, [resendSeconds]);

  // Load the DB states master once on mount (country_id = 101 by default).
  useEffect(() => {
    let isMounted = true;
    const loadStates = async () => {
      try {
        const response = await AxiosInstance.post(STATES_ENDPOINT, {
          country_id: DEFAULT_COUNTRY_ID,
        });
        if (isMounted && response.data?.status) {
          const list = response.data?.data || [];
          setStatesList(list);
          statesListRef.current = list;
        }
      } catch (error) {
        // States failed to load; matching will fall back gracefully.
        statesListRef.current = [];
      }
    };
    loadStates();
    return () => {
      isMounted = false;
    };
  }, []);

  const fetchPincodeDetails = async (pincode) => {
    setPincodeError("");
    setPincodeLookupLoading(true);
    try {
      const response = await fetch(`${POSTAL_PINCODE_API}${pincode}`);
      const data = await response.json();
      const result = data?.[0];
      const postOffices = result?.PostOffice || [];

      if (result?.Status !== "Success" || postOffices.length === 0) {
        setPincodeError("Invalid pincode. Please check and try again.");
        onFieldChange("city", null);
        onFieldChange("state", null);
        setCityOptions([]);
        setStateOptions([]);
        return;
      }

      // ✅ State comes from PostOffice[0].State → plain string
      const stateValue = postOffices[0]?.State || "";

      // ✅ City options come from District (e.g. "Chennai")
      //    Name gives locality (e.g. "Vadapalani") — use as city options
      const cityOpts = uniqueOptions(postOffices.map((po) => po.Name));

      setCityOptions(cityOpts);

      // ✅ Match the postal state name against our DB states master and store it
      //    as an option object { value: id, label: name } — same format as city.
      //    label → shown in the input, value (DB id) → sent in the payload.
      if (stateValue) {
        const matched = matchStateRecord(stateValue, statesListRef.current);
        if (matched) {
          onFieldChange("state", { value: matched.id, label: matched.name });
          setStateError("");
        } else {
          onFieldChange("state", { value: null, label: stateValue });
          setStateError(
            "State could not be matched to our records. Please contact support.",
          );
        }
      }

      // ✅ Auto-select first city option
      if (cityOpts.length > 0) {
        onFieldChange("city", cityOpts[0]);
        setCityError("");
      }
    } catch (error) {
      setPincodeError("Unable to fetch location for this pincode.");
      onFieldChange("city", null);
      onFieldChange("state", null);
      setCityOptions([]);
      setStateOptions([]);
    } finally {
      setPincodeLookupLoading(false);
    }
  };

  // Blocks the Space key from ever being inserted at the start of a field
  const blockLeadingSpaceKeyDown = (e) => {
    if (e.key === " " && e.target.selectionStart === 0) {
      e.preventDefault();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const noLeadingSpace = value.replace(/^\s+/, "");
    let nextValue = noLeadingSpace;

    if (
      name === "firstName" ||
      name === "lastName" ||
      name === "adminFirstName" ||
      name === "adminLastName"
    ) {
      nextValue = sanitizeName(noLeadingSpace);
    }

    if (name === "schoolName") {
      nextValue = sanitizeSchoolName(noLeadingSpace);
    }

    if (name === "phone") {
      if (!nextValue) {
        setPhoneError("");
      } else if (nextValue.length < 10) {
        setPhoneError("Enter a valid 10-digit mobile number");
      } else if (!PHONE_FULL_REGEX.test(nextValue)) {
        setPhoneError("Mobile number must start with 6, 7, 8, or 9");
      } else {
        setPhoneError("");
      }
    }
    if (name === "adminPhone") {
      if (!nextValue) {
        setAdminPhoneError("");
      } else if (nextValue.length < 10) {
        setAdminPhoneError("Enter a valid 10-digit mobile number");
      } else if (!PHONE_FULL_REGEX.test(nextValue)) {
        setAdminPhoneError("Mobile number must start with 6, 7, 8, or 9");
      } else {
        setAdminPhoneError("");
      }
    }

    if (name === "udiseCode") {
      nextValue = sanitizeUdise(noLeadingSpace);
    }

    if (name === "gstin") {
      nextValue = sanitizeGstin(noLeadingSpace);
    }

    if (name === "pincode") {
      nextValue = noLeadingSpace.replace(/\D/g, "").slice(0, 6);
      setPincodeError("");

      // FIX 4: if pincode is cleared or shortened below 6 digits,
      // In handleChange for pincode — when length < 6:
      if (nextValue.length < 6) {
        onFieldChange("city", null);
        onFieldChange("state", null); // ← option object cleared
        setCityOptions([]);
        setStateOptions([]);
        setCityError("");
        setStateError("");
      }

      if (nextValue.length === 6) {
        fetchPincodeDetails(nextValue);
      }
    }

    onFieldChange(name, nextValue);

    // ---- Live validation ----
    if (name === "firstName") {
      setFirstNameError(
        value && !NAME_ALLOWED_REGEX.test(value)
          ? "Only alphabets are allowed"
          : "",
      );
    }
    if (name === "lastName") {
      setLastNameError(
        value && !NAME_ALLOWED_REGEX.test(value)
          ? "Only alphabets are allowed"
          : "",
      );
    }
    if (name === "adminFirstName") {
      setAdminFirstNameError(
        value && !NAME_ALLOWED_REGEX.test(value)
          ? "Only alphabets are allowed"
          : "",
      );
    }
    if (name === "adminLastName") {
      setAdminLastNameError(
        value && !NAME_ALLOWED_REGEX.test(value)
          ? "Only alphabets are allowed"
          : "",
      );
    }
    if (name === "schoolName") {
      setSchoolNameError(
        value && !SCHOOL_NAME_ALLOWED_REGEX.test(value)
          ? "Special characters are not allowed"
          : "",
      );
    }
    if (name === "address") {
      setAddressError("");
    }
    if (name === "phone") {
      if (!nextValue) setPhoneError("");
      else if (nextValue.length < 10)
        setPhoneError("Enter a valid 10-digit phone number");
      else setPhoneError("");
    }
    if (name === "adminPhone") {
      if (!nextValue) setAdminPhoneError("");
      else if (nextValue.length < 10)
        setAdminPhoneError("Enter a valid 10-digit phone number");
      else setAdminPhoneError("");
    }
    if (name === "udiseCode") {
      if (!nextValue) setUdiseError("");
      else if (nextValue.length < 11)
        setUdiseError("UDISE code must be 11 digits");
      else setUdiseError("");
    }
    if (name === "gstin") {
      if (!nextValue) setGstinError("");
      else if (!GSTIN_REGEX.test(nextValue))
        setGstinError("Enter a valid 15-character GSTIN");
      else setGstinError("");
    }

    if (name === "email") {
      setOtpSent(false);
      onFieldChange("emailVerified", false);
      onFieldChange("emailVerifiedToken", "");
      setOtp("");
      setEmailHelperText("");
      setOtpError("");
      setResendSeconds(0);

      const trimmed = nextValue.trim();
      setEmailError(
        trimmed && !EMAIL_REGEX.test(trimmed)
          ? "Please enter a valid email address"
          : "",
      );
    }

    if (name === "shortNameUrl") {
      nextValue = sanitizeShortName(noLeadingSpace);
      onFieldChange(name, nextValue);
      onFieldChange("shortNameVerified", false);
      setShortNameSuggestions([]);
      setShortNameHelperText("");

      const trimmed = nextValue.trim();
      if (!trimmed) {
        setShortNameError("");
      } else if (URL_LIKE_REGEX.test(trimmed)) {
        setShortNameError(
          "Please enter a single name only. The URL will be generated automatically.",
        );
      } else if (trimmed.length < 3) {
        setShortNameError("Short name must be at least 3 characters");
      } else if (!SHORTNAME_REGEX.test(trimmed)) {
        setShortNameError("Use only lowercase letters, numbers, and hyphens");
      } else {
        setShortNameError("");
        setShortNameHelperText(`Preview: ${trimmed}.${SHORTNAME_BASE_URL}`);
      }
    }

    if (name === "adminEmail") {
      const trimmed = nextValue.trim();
      setAdminEmailError(
        trimmed && !EMAIL_REGEX.test(trimmed)
          ? "Please enter a valid email address"
          : "",
      );
    }
  };

  const handleSameAsOrgChange = (checked) => {
    onFieldChange("sameAsOrganization", checked);
    if (checked) {
      setAdminFirstNameError("");
      setAdminLastNameError("");
      setAdminEmailError("");
      setAdminPhoneError("");
    }
  };

  const handleSendOtp = async () => {
    const email = schoolDetails.email?.trim();
    if (!email || !EMAIL_REGEX.test(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    setEmailError("");
    setEmailHelperText("");
    setOtpError("");
    setSendingOtp(true);
    try {
      const name = `${schoolDetails.firstName || ""} ${
        schoolDetails.lastName || ""
      }`.trim();

      // status 0 → backend generates the OTP and mails it to the user
      const response = await AxiosInstance.post(EMAIL_VERIFICATION_ENDPOINT, {
        status: EMAIL_VERIFICATION_SEND,
        email,
        name,
      });

      if (response.data?.status) {
        setOtpSent(true);
        onFieldChange("emailVerified", false);
        onFieldChange("emailVerifiedToken", "");
        setOtp("");
        setOtpError("");
        setResendSeconds(OTP_RESEND_SECONDS);
        setEmailHelperText(
          response.data?.message || "Verification code sent to your email",
        );
      } else {
        setEmailError(
          response.data?.message || "Failed to send verification code",
        );
      }
    } catch (error) {
      setEmailError(
        error.response?.data?.message ||
          "Failed to send verification code. Please try again.",
      );
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    const enteredOtp = otp.trim();
    const email = schoolDetails.email?.trim();

    if (!enteredOtp) {
      setOtpError("Please enter the OTP sent to your email");
      return;
    }

    setOtpError("");
    setVerifyingOtp(true);
    try {
      // status 1 → backend validates the OTP; no frontend comparison
      const response = await AxiosInstance.post(EMAIL_VERIFICATION_ENDPOINT, {
        status: EMAIL_VERIFICATION_VERIFY,
        email,
        otp: enteredOtp,
      });

      if (response.data?.status && response.data?.data?.verified) {
        onFieldChange("emailVerified", true);
        onFieldChange(
          "emailVerifiedToken",
          response.data?.data?.verifiedToken || "",
        );
        setOtpSent(false);
        setResendSeconds(0);
        // FIX 5: clear the "please verify" error the moment verification succeeds
        setEmailError("");
        setEmailHelperText(
          response.data?.message || "Email verified successfully",
        );
      } else {
        onFieldChange("emailVerified", false);
        onFieldChange("emailVerifiedToken", "");
        setOtpError(
          response.data?.message || "Invalid verification code. Please try again.",
        );
      }
    } catch (error) {
      onFieldChange("emailVerified", false);
      onFieldChange("emailVerifiedToken", "");
      setOtpError(
        error.response?.data?.message ||
          "Failed to verify the code. Please try again.",
      );
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleCheckAvailability = async () => {
    const subDomain = schoolDetails.shortNameUrl?.trim();
    if (!subDomain) {
      setShortNameError("Please enter the short name");
      return;
    }
    if (URL_LIKE_REGEX.test(subDomain)) {
      setShortNameError(
        "Please enter a single name only. The URL will be generated automatically.",
      );
      return;
    }
    if (!SHORTNAME_REGEX.test(subDomain)) {
      setShortNameError("Use only lowercase letters, numbers, and hyphens");
      return;
    }

    setShortNameError("");
    setShortNameHelperText("");
    setShortNameSuggestions([]);
    setCheckingAvailability(true);
    try {
      const response = await AxiosInstance.post("saas/subdomain/suggestion", {
        sub_domain: subDomain,
        city: schoolDetails.city?.label || "",
      });

      if (response.data?.status && response.data?.available) {
        onFieldChange("shortNameVerified", true);
        setShortNameHelperText(
          response.data?.message ||
            `Available — ${SHORTNAME_BASE_URL}/${subDomain}`,
        );
      } else {
        onFieldChange("shortNameVerified", false);
        setShortNameError(
          response.data?.message || "Short name is not available",
        );
        setShortNameSuggestions(response.data?.data?.suggestions || []);
      }
    } catch (error) {
      onFieldChange("shortNameVerified", false);
      setShortNameError(
        error.response?.data?.message ||
          "Failed to check availability. Please try again.",
      );
    } finally {
      setCheckingAvailability(false);
    }
  };

  const handleSelectSuggestion = (suggestion) => {
    const cleaned = sanitizeShortName(suggestion).replace(/[^a-z0-9-]/g, "");
    onFieldChange("shortNameUrl", cleaned);
    setShortNameSuggestions([]);
    setShortNameError("");
    onFieldChange("shortNameVerified", true);
    setShortNameHelperText(`Available — ${SHORTNAME_BASE_URL}/${cleaned}`);
  };

  const adminFirstName = schoolDetails.sameAsOrganization
    ? schoolDetails.firstName
    : schoolDetails.adminFirstName;
  const adminLastName = schoolDetails.sameAsOrganization
    ? schoolDetails.lastName
    : schoolDetails.adminLastName;
  const adminEmail = schoolDetails.sameAsOrganization
    ? schoolDetails.email
    : schoolDetails.adminEmail;
  const adminPhone = schoolDetails.sameAsOrganization
    ? schoolDetails.phone
    : schoolDetails.adminPhone;

  // ---------- Click-based validation on Save & Preview ----------
  // FIX 1: run ALL validations at once, don't early-return on email verify.
  const handleSaveAndPreview = () => {
    let hasError = false;

    // ----- Account details -----
    if (!schoolDetails.firstName?.trim()) {
      setFirstNameError("Please enter the first name.");
      hasError = true;
    } else if (!NAME_ALLOWED_REGEX.test(schoolDetails.firstName)) {
      setFirstNameError("Only alphabets are allowed");
      hasError = true;
    } else {
      setFirstNameError("");
    }

    if (!schoolDetails.lastName?.trim()) {
      setLastNameError("Please enter the last name.");
      hasError = true;
    } else if (!NAME_ALLOWED_REGEX.test(schoolDetails.lastName)) {
      setLastNameError("Only alphabets are allowed");
      hasError = true;
    } else {
      setLastNameError("");
    }
    if (!schoolDetails.phone?.trim()) {
      setPhoneError("Please enter the phone number.");
      hasError = true;
    } else if (!PHONE_FULL_REGEX.test(schoolDetails.phone)) {
      setPhoneError(
        "Enter a valid 10-digit mobile number starting with 6, 7, 8, or 9",
      );
      hasError = true;
    } else {
      setPhoneError("");
    }

    // Email: check empty > invalid > not verified > cleared
    // FIX 5: also handles the sticky-verify-error case, since if email is
    // now verified we fall through to the `else` and clear the error.
    if (!schoolDetails.email?.trim()) {
      setEmailError("Please enter the email address.");
      hasError = true;
    } else if (!EMAIL_REGEX.test(schoolDetails.email.trim())) {
      setEmailError("Please enter a valid email address");
      hasError = true;
    } else if (!emailVerified) {
      setEmailError("Please verify your email address before continuing.");
      hasError = true;
    } else {
      setEmailError("");
    }

    // ----- School details -----
    if (!schoolDetails.schoolName?.trim()) {
      setSchoolNameError("Please enter the school name.");
      hasError = true;
    } else {
      setSchoolNameError("");
    }
    if (!schoolDetails.schoolBoard) {
      setSchoolBoardError("Please select the school board.");
      hasError = true;
    } else {
      setSchoolBoardError("");
    }
    if (!schoolDetails.schoolCategory) {
      setSchoolCategoryError("Please select the school category.");
      hasError = true;
    } else {
      setSchoolCategoryError("");
    }
    if (!schoolDetails.pincode?.trim()) {
      setPincodeError("Please enter the pincode.");
      hasError = true;
    } else if (schoolDetails.pincode.length !== 6) {
      setPincodeError("Enter a valid 6-digit pincode.");
      hasError = true;
    } else {
      setPincodeError("");
    }
    if (!schoolDetails.state) {
      setStateError("Please enter a valid pincode to auto-fill state.");
      hasError = true;
    } else if (!schoolDetails.state.value) {
      setStateError(
        "State could not be matched to our records. Please contact support.",
      );
      hasError = true;
    } else {
      setStateError("");
    }
    if (!schoolDetails.city) {
      setCityError("Please select the city.");
      hasError = true;
    } else {
      setCityError("");
    }
    if (!schoolDetails.address?.trim()) {
      setAddressError("Please enter the school address.");
      hasError = true;
    } else {
      setAddressError("");
    }

    // ----- UDISE Code (required) -----
    if (!schoolDetails.udiseCode?.trim()) {
      setUdiseError("Please enter the UDISE code.");
      hasError = true;
    } else if (!UDISE_REGEX.test(schoolDetails.udiseCode)) {
      setUdiseError("UDISE code must be 11 digits");
      hasError = true;
    } else {
      setUdiseError("");
    }

    // ----- Admin details -----
    if (!schoolDetails.sameAsOrganization) {
      if (!schoolDetails.adminFirstName?.trim()) {
        setAdminFirstNameError("Please enter the admin first name.");
        hasError = true;
      } else {
        setAdminFirstNameError("");
      }
      if (!schoolDetails.adminLastName?.trim()) {
        setAdminLastNameError("Please enter the admin last name.");
        hasError = true;
      } else {
        setAdminLastNameError("");
      }
      if (!schoolDetails.adminEmail?.trim()) {
        setAdminEmailError("Please enter the admin email.");
        hasError = true;
      } else if (!EMAIL_REGEX.test(schoolDetails.adminEmail.trim())) {
        setAdminEmailError("Please enter a valid email address");
        hasError = true;
      } else {
        setAdminEmailError("");
      }

      if (!schoolDetails.adminPhone?.trim()) {
        setAdminPhoneError("Please enter the admin phone number.");
        hasError = true;
      } else if (!PHONE_FULL_REGEX.test(schoolDetails.adminPhone)) {
        setAdminPhoneError(
          "Enter a valid 10-digit mobile number starting with 6, 7, 8, or 9",
        );
        hasError = true;
      } else {
        setAdminPhoneError("");
      }
    }

    // ----- Short Name URL -----
    if (!schoolDetails.shortNameUrl?.trim()) {
      setShortNameError("Please enter the URL.");
      hasError = true;
    } else if (URL_LIKE_REGEX.test(schoolDetails.shortNameUrl)) {
      setShortNameError(
        "Please enter a single name only. The URL will be generated automatically.",
      );
      hasError = true;
    } else if (!SHORTNAME_REGEX.test(schoolDetails.shortNameUrl)) {
      setShortNameError("Use only lowercase letters, numbers, and hyphens");
      hasError = true;
    } else if (!shortNameAvailable) {
      setShortNameError("Please check the short name availability first.");
      hasError = true;
    } else {
      setShortNameError("");
    }

    // ----- GSTIN (optional, but validate format if provided) -----
    if (schoolDetails.gstin?.trim() && !GSTIN_REGEX.test(schoolDetails.gstin)) {
      setGstinError("Enter a valid 15-character GSTIN");
      hasError = true;
    } else {
      setGstinError("");
    }

    if (!hasError) {
      onNext();
    }
  };

  return (
    <div>
      <h2 className={`heading ${styles.stepHeading}`}>
        Setup <span className="gradientText">Your School</span>
      </h2>
      <p className={styles.stepSubtitle}>
        Fill in your school details and choose your plan to get started with
        iFillip.
      </p>

      <div className={styles.stepLayout}>
        <div className={styles.formColumn}>
          {/* ---------- Account Details ---------- */}
          <div className={styles.formCard}>
            <h3 className={styles.formCardTitle}>Account Details</h3>
            <div className={styles.formGrid}>
              <CustomTextInput
                label="First Name"
                name="firstName"
                value={schoolDetails.firstName}
                onChange={handleChange}
                onKeyDown={blockLeadingSpaceKeyDown}
                error={firstNameError}
                placeholder="John"
                maxLength={120}
                required
              />
              <CustomTextInput
                label="Last Name"
                name="lastName"
                value={schoolDetails.lastName}
                onChange={handleChange}
                onKeyDown={blockLeadingSpaceKeyDown}
                error={lastNameError}
                placeholder="Doe"
                maxLength={120}
                required
              />
              <CustomTextInput
                label="Phone Number"
                name="phone"
                type="tel"
                inputMode="numeric"
                value={schoolDetails.phone}
                onChange={handleChange}
                onKeyDown={handlePhoneKeyDown}
                error={phoneError}
                maxLength={10}
                placeholder="9344772393"
                required
              />
              <CustomTextInput
                label="Email Address"
                name="email"
                type="text"
                value={schoolDetails.email}
                onChange={handleChange}
                onKeyDown={blockLeadingSpaceKeyDown}
                error={emailError}
                helperText={!emailError ? emailHelperText : ""}
                placeholder="johndoe@yourschool.edu"
                actionLabel={emailVerified ? "Verified" : "Get Code"}
                onActionClick={handleSendOtp}
                actionLoading={sendingOtp}
                actionDisabled={emailVerified || !isEmailFormatValid}
                required
              />

              {otpSent && !emailVerified && (
                <div>
                  <CustomTextInput
                    label="Email Verification Code"
                    name="otp"
                    value={otp}
                    onChange={(e) =>
                      setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    error={otpError}
                    placeholder="Enter email verification code"
                    maxLength={6}
                    inputMode="numeric"
                    disabled={resendSeconds <= 0}
                    actionLabel="Verify"
                    onActionClick={handleVerifyOtp}
                    actionLoading={verifyingOtp}
                    actionDisabled={resendSeconds <= 0 || verifyingOtp}
                    required
                  />
                  <div className={styles.resendRow}>
                    {resendSeconds > 0 ? (
                      <span className={styles.resendTimerText}>
                        Resend code in {formatTimer(resendSeconds)}
                      </span>
                    ) : (
                      <button
                        type="button"
                        className={styles.resendLink}
                        onClick={handleSendOtp}
                        disabled={sendingOtp}
                      >
                        Resend code
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ---------- School Details ---------- */}
          <div className={styles.formCard}>
            <h3 className={styles.formCardTitle}>School Details</h3>
            <div className={styles.formGrid}>
              <CustomTextInput
                label="School Name"
                name="schoolName"
                value={schoolDetails.schoolName}
                onChange={handleChange}
                onKeyDown={blockLeadingSpaceKeyDown}
                error={schoolNameError}
                placeholder="Greenfield Public School"
                maxLength={250}
                required
              />
              <CustomDropdown
                label="School Board"
                name="schoolBoard"
                value={schoolDetails.schoolBoard}
                onChange={(option) => {
                  onFieldChange("schoolBoard", option);
                  setSchoolBoardError("");
                }}
                options={SCHOOL_BOARD_OPTIONS}
                error={schoolBoardError}
                placeholder="Select board"
                required
              />
              <CustomDropdown
                label="School Category"
                name="schoolCategory"
                value={schoolDetails.schoolCategory}
                onChange={(option) => {
                  onFieldChange("schoolCategory", option);
                  setSchoolCategoryError("");
                }}
                options={SCHOOL_CATEGORY_OPTIONS}
                error={schoolCategoryError}
                placeholder="Select category"
                required
              />
              <CustomTextInput
                label="Pincode"
                name="pincode"
                type="text"
                inputMode="numeric"
                value={schoolDetails.pincode}
                onChange={handleChange}
                onKeyDown={blockLeadingSpaceKeyDown}
                error={pincodeError}
                helperText={
                  !pincodeError && pincodeLookupLoading
                    ? "Fetching city & state..."
                    : ""
                }
                maxLength={6}
                placeholder="110001"
                required
              />
              {/* FIX 4: State is disabled — value only changes via pincode lookup.
                  Stored as { value: id, label: name }; display uses .label. */}
              <CustomTextInput
                label="State"
                name="state"
                value={schoolDetails.state?.label || ""} // display DB state name
                error={stateError}
                placeholder="Auto-filled from pincode"
                disabled
                readOnly
                required
              />
              {/* City is selectable — pincode can return multiple localities */}
              <CustomDropdown
                label="City"
                name="city"
                value={schoolDetails.city}
                onChange={(option) => {
                  onFieldChange("city", option);
                  setCityError("");
                }}
                options={cityOptions}
                error={cityError}
                isLoading={pincodeLookupLoading}
                placeholder="Select city"
                required
              />
              <CustomTextInput
                label="UDISE Code"
                name="udiseCode"
                type="text"
                inputMode="numeric"
                value={schoolDetails.udiseCode || ""}
                onChange={handleChange}
                onKeyDown={blockLeadingSpaceKeyDown}
                error={udiseError}
                maxLength={11}
                placeholder="e.g. 27230400373"
                required
              />
              <div className={styles.formFullRow}>
                <CustomTextInput
                  label="School Address"
                  name="address"
                  value={schoolDetails.address}
                  onChange={handleChange}
                  onKeyDown={blockLeadingSpaceKeyDown}
                  error={addressError}
                  placeholder="Enter your school's full address"
                  multiline
                  rows={3}
                  maxLength={250}
                  required
                />
              </div>
            </div>
          </div>

          {/* ---------- School Admin Contact ---------- */}
          <div className={styles.formCard}>
            <h3 className={styles.formCardTitle}>
              School Admin Contact Details
            </h3>
            {/* FIX 3: renamed from "Same as Organization Details" */}
            <CustomCheckbox
              className={styles.sameAsOrgCheckbox}
              checked={schoolDetails.sameAsOrganization}
              onChange={(e) => handleSameAsOrgChange(e.target.checked)}
              label="Same as Account Details"
            />
            <div className={styles.formGrid}>
              <CustomTextInput
                label="First Name"
                name="adminFirstName"
                value={adminFirstName}
                onChange={handleChange}
                onKeyDown={blockLeadingSpaceKeyDown}
                error={
                  schoolDetails.sameAsOrganization ? "" : adminFirstNameError
                }
                disabled={schoolDetails.sameAsOrganization}
                placeholder="John"
                maxLength={50}
                required
              />
              <CustomTextInput
                label="Last Name"
                name="adminLastName"
                value={adminLastName}
                onChange={handleChange}
                onKeyDown={blockLeadingSpaceKeyDown}
                error={
                  schoolDetails.sameAsOrganization ? "" : adminLastNameError
                }
                disabled={schoolDetails.sameAsOrganization}
                placeholder="Doe"
                maxLength={50}
                required
              />
              <CustomTextInput
                label="Email Address"
                name="adminEmail"
                type="text"
                value={adminEmail}
                onChange={handleChange}
                onKeyDown={blockLeadingSpaceKeyDown}
                disabled={schoolDetails.sameAsOrganization}
                error={schoolDetails.sameAsOrganization ? "" : adminEmailError}
                placeholder="johndoe@yourschool.edu"
                required
              />
              <CustomTextInput
                label="Phone Number"
                name="adminPhone"
                type="tel"
                inputMode="numeric"
                value={adminPhone}
                onChange={handleChange}
                onKeyDown={handlePhoneKeyDown}
                error={schoolDetails.sameAsOrganization ? "" : adminPhoneError}
                disabled={schoolDetails.sameAsOrganization}
                maxLength={10}
                placeholder="9344772393"
                required
              />
            </div>
          </div>

          {/* ---------- Short Name URL ---------- */}
          <div className={styles.formCard}>
            <h3 className={styles.formCardTitle}>Short Name URL</h3>
            <CustomTextInput
              label="Short Name"
              name="shortNameUrl"
              value={schoolDetails.shortNameUrl}
              onChange={handleChange}
              onKeyDown={blockLeadingSpaceKeyDown}
              error={shortNameError}
              helperText={!shortNameError ? shortNameHelperText : ""}
              placeholder="e.g. ags"
              maxLength={50}
              actionLabel={
                shortNameAvailable ? "Available" : "Check Availability"
              }
              onActionClick={handleCheckAvailability}
              actionLoading={checkingAvailability}
              actionDisabled={shortNameAvailable}
              required
            />

            {shortNameSuggestions.length > 0 && (

              <div className={styles.subdomainSuggestions}>
                <span className={styles.subdomainSuggestionsLabel}>
                  Try one of these instead:
                </span>
                <div className={styles.subdomainSuggestionsList}>
                  {shortNameSuggestions.map((suggestion) => (
                    <button
                      type="button"
                      key={suggestion}
                      className={styles.subdomainSuggestionChip}
                      onClick={() => handleSelectSuggestion(suggestion)}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ---------- Billing Information ---------- */}
          <div className={styles.formCard}>
            <h3 className={styles.formCardTitle}>Billing Information</h3>
            <div className={styles.formGrid}>
              <div className={styles.formFullRow}>
                <CustomTextInput
                  label="GSTIN (Optional)"
                  name="gstin"
                  value={schoolDetails.gstin || ""}
                  onChange={handleChange}
                  onKeyDown={blockLeadingSpaceKeyDown}
                  error={gstinError}
                  placeholder="e.g. 07AAAAA1111A1Z1"
                  maxLength={15}
                />
              </div>
            </div>
          </div>
        </div>

        <BillingSummary
          plan={plan}
          slab={slab}
          addOns={addOns}
          selectedAddOnIds={selectedAddOnIds}
          primaryLabel="Save & Preview"
          onPrimary={handleSaveAndPreview}
          secondaryLabel="Back to Add-ons"
          onSecondary={onBack}
        />
      </div>
    </div>
  );
};

export default Step3SchoolDetails;